import EventEmitter from "eventemitter3";
import { EventTypes } from "../../../../../core/event-bus";
import { LocalizationService } from "../../../../../services/localization-service";
import { HalfModifierSelection } from "../../../../../types/enums";
import { Util } from "../../../../../utils/util";
import { SegmentedButton } from "../../../../shared/SegmentedButton/segmented-button";
import { ISegmentedButtonData, ISegmentedButtonDataButton } from "../../../../shared/SegmentedButton/segmented-button.interface";

interface IModifierHalfSelectionState {
    modifierDisplayGroupId: number,
    modifierId: number,
    halfId: HalfModifierSelection
}

export interface ICustomizeItemModifierHalfBarClickEvent {
    modifierDisplayGroupId: number,
    modifierId: number,
    halfId: HalfModifierSelection
}

/**
 * Manages the UI element for selecting a half (left/whole/right) modifier on a customizable item.
 */
export class CustomizeItemModifierHalfBar {
    private readonly _eventBus: EventEmitter;
    private readonly _localizationService: LocalizationService;
    private _containerElement: HTMLElement;
    private readonly _selectionState: IModifierHalfSelectionState;

    /**
     * @param {EventEmitter} eventBus - The shared event bus for app-wide communication.
     * @param {LocalizationService} localizationService - Service used for getting localized strings.
     * @param {number} modifierDisplayGroupId - ID of the modifier's display group.
     * @param {number} modifierId - ID of the modifier this bar is associated with.
     * @param {HalfModifierSelection} halfId - Initial selected half (left/whole/right).
     * @param {boolean} hidden - Whether the half bar should be initially hidden.
     */
    constructor(eventBus: EventEmitter, localizationService: LocalizationService, modifierDisplayGroupId: number, modifierId: number, halfId: HalfModifierSelection, hidden: boolean) {
        this._eventBus = eventBus;
        this._localizationService = localizationService;

        this._selectionState = this._initHalfSelectionState(modifierDisplayGroupId, modifierId, halfId);
        this._containerElement = this._createHalfableElement(hidden);
    }

    /**
     * Returns the root HTML element representing the modifier half bar.
     * 
     * @returns {HTMLElement} The root container element.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Clears the current element by replacing it with a fresh, empty `<div>`.
     * Used for resetting or unmounting the component.
     */
    public clearElement(): void {
        this._containerElement = document.createElement("div");
    }

    /**
     * Creates a new container element and re-renders the halfable bar.
     * 
     * @param {boolean} hidden - Whether the element should be hidden initially.
     */
    public createElement(hidden: boolean): void {
        this._containerElement = this._createHalfableElement(hidden);
    }

    /**
     * Initializes the selection state for this half bar component.
     *
     * @param {number} modifierDisplayGroupId - The display group ID for the modifier.
     * @param {number} modifierId - The specific modifier ID.
     * @param {HalfModifierSelection} halfId - The half selection (LEFT, WHOLE, RIGHT).
     * @returns {IModifierHalfSelectionState} The constructed selection state.
     */
    private _initHalfSelectionState(modifierDisplayGroupId: number, modifierId: number, halfId: HalfModifierSelection): IModifierHalfSelectionState {
        return {
            modifierDisplayGroupId,
            modifierId,
            halfId
        };
    }

    /**
     * Creates the wrapper container and appends the segmented selection bar to it.
     *
     * @param {boolean} hidden - Whether the container should be hidden initially.
     * @returns {HTMLElement} The container with the selection bar appended.
     */
    private _createHalfableElement(hidden: boolean): HTMLElement {
        const element = document.createElement("div");
        element.id = `div_customize_item_modifier_halfable_selection_${this._selectionState.modifierDisplayGroupId}_${this._selectionState.modifierId}`;
        element.classList.add("customize-item-premodifier-selection");

        const barElement = this._createHalfableSelectionBar();
        element.appendChild(barElement);

        if (hidden)
            Util.hideElement(element);

        return element;
    }

    /**
     * Builds the segmented button UI with three buttons: "Left", "Whole", and "Right".
     * Each button corresponds to a halfable selection type.
     *
     * @returns {HTMLElement} A fully configured segmented button element.
     */
    private _createHalfableSelectionBar(): HTMLElement {
        const segmentedData: ISegmentedButtonData = {
            buttonId: `customize_item_halfable_${this._selectionState.modifierDisplayGroupId}_${this._selectionState.modifierId}`,
            ariaGroupLabel: "Modifier Half Selection",
            buttons: [
                this._createHalfSegmentedButtonConfig(
                    this._localizationService.names("Left"),
                    "icon-item-half-left",
                    HalfModifierSelection.LEFT,
                    this._selectionState.halfId === HalfModifierSelection.LEFT
                ),
                this._createHalfSegmentedButtonConfig(
                    this._localizationService.names("Whole"),
                    "icon-item-whole",
                    HalfModifierSelection.WHOLE,
                    this._selectionState.halfId === HalfModifierSelection.WHOLE
                ),
                this._createHalfSegmentedButtonConfig(
                    this._localizationService.names("Right"),
                    "icon-item-half-right",
                    HalfModifierSelection.RIGHT,
                    this._selectionState.halfId === HalfModifierSelection.RIGHT
                ),
            ]
        };

        const segmentedButton = new SegmentedButton(segmentedData, this._localizationService);
        return segmentedButton.element;
    }

    /**
     * Creates a configuration object for a single button within the halfable segmented control.
     * Each button includes icon/text HTML and a click handler that triggers half selection change.
     *
     * @param {string} text - The display label for the button.
     * @param {string} icon - The icon class to apply.
     * @param {HalfModifierSelection} halfId - The half type this button represents.
     * @param {boolean} isSelected - Indicates whether this button is initially selected.
     * @returns {ISegmentedButtonDataButton} The configuration object for the button.
     */
    private _createHalfSegmentedButtonConfig(
        text: string,
        icon: string,
        halfId: HalfModifierSelection,
        isSelected: boolean
    ): ISegmentedButtonDataButton {
        const buttonText = this._createHalfButtonText(text, icon);
        return {
            id: halfId,
            text: buttonText,
            selected: isSelected,
            function: (event: Event): boolean => {
                if (!event.isTrusted)
                    return false;

                return this._onHalfSelectionChange(halfId);
            }
        };
    }

    /**
     * Generates the inner HTML markup for a button in the halfable segmented control.
     *
     * @param {string} text - The button label text.
     * @param {string} icon - The CSS class for the button's icon.
     * @returns {string} The HTML string to be rendered inside the button.
     */
    private _createHalfButtonText(text: string, icon: string): string {
        return `<div class="customize-item-half-button">
                    <span class="${icon}" aria-hidden="true"></span>
                    <span>${text}</span>
                </div>`;
    }

    /**
     * Handles user selection of a different half (left/whole/right).
     * Emits an event with the updated selection if it changed.
     *
     * @param {HalfModifierSelection} halfId - The selected half.
     * @returns {boolean} True if the selection changed, false if it was already selected.
     */
    private readonly _onHalfSelectionChange = (halfId: HalfModifierSelection): boolean => {
        if (this._selectionState.halfId === halfId)
            return false;

        this._selectionState.halfId = halfId;

        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_MODIFIER_HALF_CLICKED, {
            modifierDisplayGroupId: this._selectionState.modifierDisplayGroupId,
            modifierId: this._selectionState.modifierId,
            halfId
        } as ICustomizeItemModifierHalfBarClickEvent);

        return true;
    }
}